import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { CalendarSearch } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { redirect, useNavigate, useParams } from 'react-router';

import {
  useData,
  useEarliestBookableDate,
  useFetchDateForMonth,
  useMonthlySlots,
} from '@virtual-care/hooks';
import {
  Calendar,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@virtual-care/shadcn';
import { Loading, TimeSlots, type UiTimeSlot } from '@virtual-care/ui';

import AppointmentTimeFooter from '../../components/booking/time-slots-footer';
import { ProfileCard } from '../../components/header';
import Header from '../../components/header/header';
import { useReservation } from '../../hooks/reservation/context.tsx';
import {
  type StandardReservation,
  standardReservationStore,
} from '../../hooks/reservation/standard.ts';

export default function AppointmentTime() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const earliestBookableDate = useEarliestBookableDate();
  const [date, setDate] = useState<Date | undefined>(earliestBookableDate);
  const [selectedSlot, setSelectedSlot] = useState<UiTimeSlot | undefined>();
  const [displayedMonth, setDisplayedMonth] =
    useState<Date>(earliestBookableDate);
  const { specialtyId } = useParams();
  const [reservation, setReservation] = useReservation<StandardReservation>();
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const fetchDate = useFetchDateForMonth(displayedMonth);
  const { data: monthlyData, isFetching } = useData(
    'GET /availability/slots',
    {
      specialityId: specialtyId as string,
      date: fetchDate.toISOString(),
    },
    { enabled: !!specialtyId },
  );
  const { disabledDates, selectedDateSlots } = useMonthlySlots(
    monthlyData,
    date,
  );

  const handleBooking = async () => {
    if (!date || !selectedSlot) return;
    if (!reservation) {
      navigate('/book-appointment');
      return;
    }

    const appointmentDate = format(selectedSlot.datetime, 'yyyy-MM-dd');
    setReservation({
      ...reservation,
      scheduledDate: `${appointmentDate}T00:00:00.000Z`,
      scheduledMinutes: selectedSlot.startTime,
      timezone,
    });

    navigate('/book-appointment/booking-confirmation');
  };

  const calendarLocale = i18n.language === 'en' ? enUS : ar;

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-sidebar-primary rounded-full p-2">
              <CalendarSearch className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('booking.appointmentTitle')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>
      <div className="card-container">
        <Card className="card gap-0">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-bold">
              {t('booking.appointmentTime.title')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t('booking.appointmentTime.subtitle')}
            </p>
          </CardHeader>
          <CardContent className="relative flex w-full flex-col items-center justify-center p-0 md:pe-48">
            {isFetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
                <Loading />
              </div>
            )}
            <div className="p-6">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(value) => {
                  setDate(value);
                  setSelectedSlot(undefined);
                }}
                month={displayedMonth}
                onMonthChange={setDisplayedMonth}
                disabled={[{ before: earliestBookableDate }, ...disabledDates]}
                showOutsideDays={false}
                className="bg-transparent p-0 [--cell-size:--spacing(10)] md:[--cell-size:--spacing(12)]"
                locale={calendarLocale}
              />
            </div>
            {date && (
              <div className="no-scrollbar inset-y-0 inset-e-0 flex w-full scroll-pb-6 flex-col gap-4 overflow-y-auto border-t p-6 md:absolute md:max-h-none md:w-48 md:border-s md:border-t-0">
                <TimeSlots
                  date={date}
                  timezone={timezone}
                  slots={selectedDateSlots}
                  selectedSlot={selectedSlot}
                  onSelectSlot={setSelectedSlot}
                  className="md:grid-cols-1"
                />
              </div>
            )}
          </CardContent>
          <AppointmentTimeFooter
            datetime={selectedSlot?.datetime}
            onBack={() => navigate(-1)}
            onContinue={handleBooking}
          />
        </Card>
      </div>
    </>
  );
}

export function appointmentTimeLoader() {
  if (!standardReservationStore.read()) {
    throw redirect('/book-appointment');
  }
  return null;
}
